// PrintBusinessWallet.jsx
import React from "react"
import profile from "assets/images/ecs234.jpg"

/**
 * Props:
 * - business: { id, businessName, email, phone, address? }
 * - summary:  { leads_count, priced_leads_count, cod_collected, service_charges, payouts, adjustments, wallet_balance, range }
 * - ledger:   Array ledger rows (optional to show condensed totals)
 * - payouts:  Array (optional compact table - last N)
 * - adjustments: Array (optional compact table - last N)
 * - issuedAt: string (ISO)
 * - from, to: "YYYY-MM-DD" strings for the selected range
 */
export default function PrintBusinessWallet({
  business,
  summary,
  ledger = [],
  payouts = [],
  adjustments = [],
  issuedAt,
  from,
  to,
}) {
  const asPKR = v =>
    Number(v || 0).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })

  return (
    <div className="pbw-root">
      {/* Header */}
      <div className="pbw-header">
        <div className="pbw-brand">
          <div className="pbw-logo">
            {profile ? (
              <img
                src={profile}
                alt={business?.name || "Logo"}
                onError={e => {
                  // hide the broken <img> and show initials fallback
                  e.currentTarget.style.display = "none"
                  const sib = e.currentTarget.nextElementSibling
                  if (sib) sib.style.display = "flex"
                }}
              />
            ) : null}
            <span
              className="pbw-logo-initials"
              style={{ display: profile ? "none" : "flex" }}
              aria-hidden={!!profile}
              title={business?.name || "Business"}
            >
              {(business?.name || "BW").slice(0, 2).toUpperCase()}
            </span>
          </div>

          <div>
            <div className="pbw-title">Business Wallet Statement</div>
            <div className="pbw-sub">
              Statement Period: {from || "—"} → {to || "—"}
            </div>
          </div>
        </div>

        <div className="pbw-meta">
          <div>
            <strong>Issued:</strong> {new Date(issuedAt).toLocaleString()}
          </div>
          <div>
            <strong>Statement #:</strong> BW-{business?.id}-
            {(Date.now() + "").slice(-6)}
          </div>
        </div>
      </div>

      {/* Business block */}
      <div className="pbw-block">
        <div className="pbw-h2">Business</div>
        <div className="pbw-cols">
          <div>
            <div>
              <strong>{business?.businessName || "—"}</strong>
            </div>
            {business?.address ? (
              <div className="muted">{business.address}</div>
            ) : null}
            <div className="muted">ID #{business?.id}</div>
          </div>
          <div className="pbw-right">
            <div>{business?.email || "—"}</div>
            <div>{business?.phone || "—"}</div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="pbw-grid">
        <div className="pbw-card">
          <div className="pbw-kpi-label">Total Leads</div>
          <div className="pbw-kpi-value">{summary?.leads_count ?? "—"}</div>
        </div>
        <div className="pbw-card">
          <div className="pbw-kpi-label">Priced & Assigned</div>
          <div className="pbw-kpi-value">
            {summary?.priced_leads_count ?? "—"}
          </div>
        </div>
        <div className="pbw-card">
          <div className="pbw-kpi-label">COD Collected</div>
          <div className="pbw-kpi-value">
            PKR {asPKR(summary?.cod_collected)}
          </div>
        </div>
        <div className="pbw-card">
          <div className="pbw-kpi-label">Service Charges</div>
          <div className="pbw-kpi-value">
            PKR {asPKR(summary?.service_charges)}
          </div>
        </div>
        <div className="pbw-card">
          <div className="pbw-kpi-label">Adjustments</div>
          <div className="pbw-kpi-value">PKR {asPKR(summary?.adjustments)}</div>
        </div>
        <div className="pbw-card">
          <div className="pbw-kpi-label">Payouts</div>
          <div className="pbw-kpi-value">PKR {asPKR(summary?.payouts)}</div>
        </div>
      </div>

      {/* Balance breakdown */}
      <div className="pbw-block">
        <div className="pbw-h2">Balance Breakdown</div>
        <table className="pbw-table compact">
          <tbody>
            <tr>
              <td>COD Collected</td>
              <td className="right">PKR {asPKR(summary?.cod_collected)}</td>
            </tr>
            <tr>
              <td className="muted">Service Charges</td>
              <td className="right muted">
                - PKR {asPKR(summary?.service_charges)}
              </td>
            </tr>
            <tr>
              <td className="muted">Payouts</td>
              <td className="right muted">- PKR {asPKR(summary?.payouts)}</td>
            </tr>
            <tr>
              <td className="muted">Adjustments</td>
              <td className="right">
                {Number(summary?.adjustments) < 0 ? "- " : "+ "}PKR{" "}
                {asPKR(Math.abs(summary?.adjustments || 0))}
              </td>
            </tr>
            <tr className="total">
              <td>Wallet Balance (Net Payable to Business)</td>
              <td className="right">PKR {asPKR(summary?.wallet_balance)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Optional compact histories (recent) */}
      <div className="pbw-cols2">
        <div className="pbw-half">
          <div className="pbw-h2">Recent Payouts</div>
          <table className="pbw-table compact">
            <thead>
              <tr>
                <th>Time</th>
                <th className="right">Amount</th>
                <th>Ref</th>
              </tr>
            </thead>
            <tbody>
              {(payouts || []).slice(0, 8).map(p => (
                <tr key={p.id}>
                  <td>{new Date(p.created_at).toLocaleString()}</td>
                  <td className="right">- {asPKR(p.amount)}</td>
                  <td className="ellipsis">{p.reference || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!payouts || payouts.length === 0) && (
            <div className="pbw-empty">No payouts in range</div>
          )}
        </div>
        <div className="pbw-half">
          <div className="pbw-h2">Recent Adjustments</div>
          <table className="pbw-table compact">
            <thead>
              <tr>
                <th>Time</th>
                <th className="right">Amount</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {(adjustments || []).slice(0, 8).map(a => (
                <tr key={a.id}>
                  <td>{new Date(a.created_at).toLocaleString()}</td>
                  <td
                    className={`right ${Number(a.amount) < 0 ? "neg" : "pos"}`}
                  >
                    {Number(a.amount) < 0 ? "- " : "+ "}
                    {asPKR(Math.abs(a.amount))}
                  </td>
                  <td className="ellipsis">{a.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!adjustments || adjustments.length === 0) && (
            <div className="pbw-empty">No adjustments in range</div>
          )}
        </div>
      </div>

      <div className="pbw-footer">
        <div className="muted">
          Generated by ECS • {new Date(issuedAt).toLocaleString()}
        </div>
        <div className="muted">
          This is a system generated statement and does not require a signature.
        </div>
      </div>

      {/* Print-only styles */}
      <style>{`
        .pbw-root {
          font-family: ui-sans-serif, system-ui, Segoe UI, Roboto, Helvetica, Arial, 'Apple Color Emoji','Segoe UI Emoji';
          color: #111827;
          padding: 24px;
          width: 210mm;
          min-height: 297mm;
          box-sizing: border-box;
        }
          .pbw-brand{
  display:flex;
  align-items:center;
  gap:14px;
  min-width:0; /* prevent overflow */
}

.pbw-logo{
  width:72px;
  height:72px;
  border-radius:12px;
  background:#ffffff;
  border:1px solid #e5e7eb;
  box-shadow:0 2px 8px rgba(0,0,0,.06);
  display:flex;
  align-items:center;
  justify-content:center;
  overflow:hidden;
}

.pbw-logo img{
  width:100%;
  height:100%;
  object-fit:contain;      /* keeps proportions for tall/wide logos */
  padding:6px;             /* breathing room */
}

.pbw-logo-initials{
  width:100%;
  height:100%;
  align-items:center;
  justify-content:center;
  font-weight:800;
  font-size:20px;
  color:#111827;
  background:linear-gradient(180deg,#f9fafb,#f3f4f6);
  letter-spacing:.5px;
  border-radius:12px;
  display:flex;
}
        .pbw-title{
  font-size:18px;
  font-weight:800;
  letter-spacing:.2px;
  color:#111827;
  line-height:1.1;
}
        .pbw-sub{
  font-size:12px;
  color:#6b7280;
  margin-top:2px;
}
        .pbw-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom: 12px; }
        .pbw-meta{
  text-align:right;
  font-size:12px;
  color:#4b5563;
}
  .pbw-empty{
  border:1px dashed #d1d5db;
  background:#f9fafb;
  color:#6b7280;
  font-size:10px;
  margin-top:5px;
  padding:5px;
  text-align:center;
  border-radius:5px;

  /* Keep the block from splitting across pages */
  break-inside: avoid;
  page-break-inside: avoid;
}
        .pbw-block { margin-top:16px; }
        .pbw-h2 { font-weight:700; font-size:14px; margin-bottom:8px; }
        .pbw-cols { display:flex; gap:16px; align-items:flex-start; }
        .pbw-cols2 { display:flex; gap:16px; align-items:flex-start;margin-top:10px; }
        .pbw-right { margin-left:auto; text-align:right; }
        .pbw-grid { display:grid; grid-template-columns: repeat(3, 1fr); gap:10px; margin-top:12px; }
        .pbw-card { border:1px solid #e5e7eb; border-radius:8px; padding:12px; }
        .pbw-kpi-label { color:#6b7280; font-size:12px; }
        .pbw-kpi-value { font-size:18px; font-weight:800; }
        .pbw-table { width:100%; border-collapse: collapse; }
        .pbw-table th, .pbw-table td { border:1px solid #e5e7eb; padding:6px 8px; font-size:12px; }
        .pbw-table.compact th, .pbw-table.compact td { padding:5px 6px; }
        .pbw-table .right { text-align:right; }
        .pbw-table .center { text-align:center; }
        .pbw-table .neg { color:#b91c1c; }
        .pbw-table .pos { color:#065f46; }
        .pbw-table .ellipsis { max-width:220px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .pbw-table tr.total td { font-weight:800; background:#f9fafb; }
        .muted { color:#6b7280; }
        .pbw-half { width:50%; }
        .pbw-footer { margin-top:16px; font-size:11px; display:flex; justify-content:space-between; color:#6b7280; }

        @media print {
          @page { size: A4; margin: 10mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .pbw-root { box-shadow:none; padding:0; width:auto; min-height:auto; }
        }
          .pbw-meta{
  text-align:right;
  font-size:12px;
  color:#4b5563;
}
      `}</style>
    </div>
  )
}
